import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  parentChildReportTemplatesAssignPath,
  parentChildReportTemplatesUnassignPath,
} from '~/hooks/analyticsStatAccess/parentChildReportTemplatesApiPaths';
import { staffConfigModeQueryParams } from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { invalidateClientAnalyticsQueries } from '~/hooks/mutations/useStaffClientReportTemplateAssignMutations/invalidateClientAnalyticsQueries';
import { parentChildReportTemplatesQueryKey } from '~/hooks/queries/useParentChildReportTemplatesQuery';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import {
  AnalyticsStatsApiError,
  flattenAnalyticsStatsErrorResponse,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsErrorUtils';

export type ParentAssignResult = {
  /** Parent’s assigned template public id from the request. */
  assignedPublicId: string;
  status: 'assigned' | 'skipped' | 'failed';
  /** New assigned copy on the child, when status is assigned. */
  childAssignedPublicId?: string;
  code?: string;
  message?: string;
};

export type ParentUnassignResult = {
  assignedPublicId: string;
  status: 'unassigned' | 'failed';
  code?: string;
  message?: string;
};

type AssignResponse = { results: ParentAssignResult[] };
type UnassignResponse = { results: ParentUnassignResult[] };

const assignParentChildReportTemplates = async (params: {
  parentAccountId: string;
  childClientId: string;
  /** Parent’s assigned template public ids. */
  assignedPublicIds: string[];
  analyticsReportingMode?: Genie2ReportingMode | null;
}): Promise<AssignResponse> => {
  const { parentAccountId, childClientId, assignedPublicIds, analyticsReportingMode } = params;
  const { data, status } = await customFetch.post<unknown>(
    parentChildReportTemplatesAssignPath(parentAccountId, childClientId),
    JSON.stringify({ assignedPublicIds }),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      params: staffConfigModeQueryParams(analyticsReportingMode),
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return data as AssignResponse;
};

const unassignParentChildReportTemplates = async (params: {
  parentAccountId: string;
  childClientId: string;
  /** Child’s assigned copy public ids. */
  assignedPublicIds: string[];
  analyticsReportingMode?: Genie2ReportingMode | null;
}): Promise<UnassignResponse> => {
  const { parentAccountId, childClientId, assignedPublicIds, analyticsReportingMode } = params;
  const { data, status } = await customFetch.post<unknown>(
    parentChildReportTemplatesUnassignPath(parentAccountId, childClientId),
    JSON.stringify({ assignedPublicIds }),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      throwOnHTTPError: false,
      params: staffConfigModeQueryParams(analyticsReportingMode),
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }
  return data as UnassignResponse;
};

export function useAssignParentChildReportTemplatesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignParentChildReportTemplates,
    mutationKey: ['parent-child', 'report-templates', 'assign'],
    onSettled: (_, __, variables) => {
      const accountId = Number(variables.childClientId);
      queryClient.invalidateQueries({
        queryKey: parentChildReportTemplatesQueryKey(
          variables.parentAccountId,
          variables.childClientId,
        ),
      });
      if (Number.isFinite(accountId) && accountId > 0) {
        invalidateClientAnalyticsQueries(queryClient, accountId, 'client');
      }
    },
  });
}

export function useUnassignParentChildReportTemplatesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unassignParentChildReportTemplates,
    mutationKey: ['parent-child', 'report-templates', 'unassign'],
    onSettled: (_, __, variables) => {
      const accountId = Number(variables.childClientId);
      queryClient.invalidateQueries({
        queryKey: parentChildReportTemplatesQueryKey(
          variables.parentAccountId,
          variables.childClientId,
        ),
      });
      if (Number.isFinite(accountId) && accountId > 0) {
        invalidateClientAnalyticsQueries(queryClient, accountId, 'client');
      }
    },
  });
}
